import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { trustPortalApi, TrustPortalPublicData } from '../api/trustPortal.api';
import PageInfoButton from '@/components/PageInfoButton';

const TrustPortalPublic: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<TrustPortalPublicData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        const res = await trustPortalApi.getPublicPortal(slug);
        setData(res);
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [slug]);

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent mx-auto mb-4" />
        <p className="text-slate-500">Loading trust portal...</p>
      </div>
    </div>
  );

  if (notFound || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center max-w-md mx-auto p-8">
        <div className="text-5xl mb-4">🔒</div>
        <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 mb-2">Portal Not Found</h1><PageInfoButton /></div>
        <p className="text-slate-500">This trust portal is either not public or doesn't exist.</p>
      </div>
    </div>
  );

  const accent = data.accent_color || '#059669';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header style={{ backgroundColor: accent }} className="text-white py-12 px-6">
        <div className="max-w-3xl mx-auto">
          {data.company_logo && (
            <img src={data.company_logo} alt={data.company_name} className="h-12 mb-4 object-contain" />
          )}
          <h1 className="text-3xl font-bold mb-2">{data.company_name}</h1>
          <p className="text-lg opacity-90">
            {data.custom_headline || 'Security & Compliance Status'}
          </p>
          {data.custom_description && (
            <p className="mt-3 opacity-80 text-sm max-w-xl">{data.custom_description}</p>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto py-10 px-6 space-y-8">
        {/* Compliance Score */}
        {data.show_compliance_score && data.compliance_score != null && (
          <div className="bg-white rounded-xl border p-6 text-center">
            <div className="text-5xl font-bold mb-2" style={{ color: accent }}>{data.compliance_score}%</div>
            <div className="text-slate-600 font-medium">Overall Compliance Score</div>
          </div>
        )}

        {/* Certifications */}
        {data.show_certifications && data.certifications && data.certifications.length > 0 && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">🏆 Active Certifications</h2>
            <div className="space-y-3">
              {data.certifications.map((cert, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: `${accent}10` }}>
                  <div>
                    <div className="font-semibold text-slate-900">{cert.name || cert.certification_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Issued {new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(cert.valid_until || cert.expires_at) && (
                      <span className="text-xs text-slate-500">
                        Valid until {new Date((cert.valid_until || cert.expires_at)!).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    <span className="text-xs px-2 py-1 rounded-full font-medium text-white" style={{ backgroundColor: accent }}>
                      Active
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Frameworks */}
        {data.show_frameworks && data.frameworks && data.frameworks.length > 0 && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">📋 Compliance Frameworks</h2>
            <div className="space-y-3">
              {data.frameworks.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                  <div className="font-medium text-slate-900">{f.name || f.framework_name}</div>
                  <div className="flex items-center gap-3">
                    {(f.compliance_score != null) && (
                      <span className="text-sm font-medium" style={{ color: accent }}>{f.compliance_score}%</span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full capitalize font-medium ${
                      f.status === 'certified' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {f.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata row */}
        {(data.show_last_audit_date || data.show_controls_count) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.show_last_audit_date && data.last_audit_date && (
              <div className="bg-white rounded-xl border p-5 text-center">
                <div className="text-3xl font-bold text-slate-900 mb-1">
                  {new Date(data.last_audit_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </div>
                <div className="text-sm text-slate-500">Last Audit Date</div>
              </div>
            )}
            {data.show_controls_count && data.controls_count != null && (
              <div className="bg-white rounded-xl border p-5 text-center">
                <div className="text-2xl font-bold mb-1" style={{ color: accent }}>{data.controls_count}</div>
                <div className="text-sm text-slate-500">Certified Controls</div>
              </div>
            )}
          </div>
        )}

        {/* Sustainability Score */}
        {data.show_sustainability_score && data.sustainability_score != null && data.sustainability_score > 0 && (
          <div className="bg-white rounded-xl border p-6 text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-3">🌱 Sustainability Score</h2>
            <div className="text-5xl font-bold mb-2" style={{ color: accent }}>{data.sustainability_score}%</div>
            <div className="text-slate-500 text-sm">Composite score from emission reductions, offsets, and credits</div>
          </div>
        )}

        {/* Emission Reduction Targets */}
        {data.show_emission_targets && data.emission_targets && data.emission_targets.length > 0 && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">🎯 Emission Reduction Targets</h2>
            <div className="space-y-4">
              {data.emission_targets.map((t: any, i: number) => (
                <div key={i} className="p-4 rounded-xl bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-slate-900">{t.name}</div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${t.on_track ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                        {t.on_track ? 'On Track' : 'Behind'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${t.status === 'achieved' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {t.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    Scope: {t.scope} · {t.baseline_year} → {t.target_year} · {t.target_type === 'percentage' ? `${t.target_value}% reduction` : `Target: ${t.target_value} tCO₂e`}
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, t.percent_achieved)}%`, backgroundColor: accent }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 mt-1 text-right">{t.percent_achieved}% achieved</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scope Breakdown */}
        {data.show_scope_breakdown && data.scope_breakdown && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">📊 Emissions by Scope</h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {[
                { label: 'Scope 1', value: data.scope_breakdown.scope1_total || data.scope_breakdown.scope1_tco2e, desc: 'Direct emissions' },
                { label: 'Scope 2', value: data.scope_breakdown.scope2_total || data.scope_breakdown.scope2_tco2e, desc: 'Purchased energy' },
                { label: 'Scope 3', value: data.scope_breakdown.scope3_total || data.scope_breakdown.scope3_tco2e, desc: 'Value chain' },
              ].map((s, i) => (
                <div key={i} className="text-center p-3 rounded-xl bg-slate-50">
                  <div className="text-2xl font-bold text-slate-900">{s.value != null ? (Number(s.value) < 0.1 ? Number(s.value).toFixed(4) : Number(s.value) < 10 ? Number(s.value).toFixed(2) : Number(s.value).toFixed(1)) : '—'}</div>
                  <div className="text-xs text-slate-500">{s.label} tCO₂e</div>
                  <div className="text-[10px] text-slate-400">{s.desc}</div>
                </div>
              ))}
            </div>
            {data.scope_breakdown.report_period && (
              <div className="text-xs text-slate-400 text-center">Period: {data.scope_breakdown.report_period}</div>
            )}
          </div>
        )}

        {/* Carbon Credits */}
        {data.show_carbon_credits && data.carbon_credits && data.carbon_credits.total_active > 0 && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">🌿 Carbon Credits</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <div className="text-2xl font-bold" style={{ color: accent }}>{data.carbon_credits.total_active}</div>
                <div className="text-xs text-slate-500">Active Credits</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <div className="text-2xl font-bold" style={{ color: accent }}>{Number(data.carbon_credits.total_volume_tonnes).toFixed(1)}</div>
                <div className="text-xs text-slate-500">Total tCO₂e</div>
              </div>
            </div>
            {data.carbon_credits.credits?.length > 0 && (
              <div className="space-y-2">
                {data.carbon_credits.credits.map((c: any, i: number) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-sm">
                    <span className="font-medium text-slate-800">{c.name}</span>
                    <span className="text-slate-500">{c.credit_type} · {c.volume_tonnes} tCO₂e</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Emission Offsets */}
        {data.show_emission_offsets && data.emission_offsets && data.emission_offsets.total_tco2e > 0 && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">♻️ Emission Offsets</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <div className="text-xl font-bold text-slate-900">{data.emission_offsets.total_credits}</div>
                <div className="text-[10px] text-slate-500">Total Credits</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <div className="text-xl font-bold" style={{ color: accent }}>{Number(data.emission_offsets.total_tco2e).toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">Total tCO₂e</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-emerald-50">
                <div className="text-xl font-bold text-emerald-700">{Number(data.emission_offsets.verified_tco2e).toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">Verified tCO₂e</div>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <div className="text-xl font-bold text-slate-600">{Number(data.emission_offsets.retired_tco2e).toFixed(1)}</div>
                <div className="text-[10px] text-slate-500">Retired tCO₂e</div>
              </div>
            </div>
            {data.emission_offsets.by_registry?.length > 0 && (
              <div className="space-y-2">
                {data.emission_offsets.by_registry.map((r: any, i: number) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-sm">
                    <span className="font-medium text-slate-800">{r.registry}</span>
                    <span className="text-slate-500">{r.count} credits · {Number(r.total_tco2e).toFixed(1)} tCO₂e</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 pt-4">
          Powered by EIS · Compliance Management Platform
        </div>
      </div>
    </div>
  );
};

export default TrustPortalPublic;
